import { Stack } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { useCallback } from 'react'
import { EVENT } from '../../../../../constants'
import {
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../models/fulfilment'
import { useSaleInventoryData } from '../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import { selectIsEcomOrder } from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  setLineItemFulfilmentNoteError,
} from '../../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { FulfilmentSelect } from '../../../../react/modals/mark-as-unfulfilled-modal/Steps/Step1/FulfillmentSelect'
import { NoteError } from '../../../../shared/NoteInput'
import { LineItemFulfilmentAddress } from '../LineItemFulfilmentAddress'
import { LineItemFulfilmentNote } from '../LineItemFulfilmentNote'
import { OutdatedTaxCategoryWarning } from '../OutdatedTaxCategoryWarning'
import { useAutoPopulateFulfillmentAddresses } from '../hook/useAutoPopulateFulfillmentAddresses'
import { useSaleFulfilment } from '../hook/useSaleFulfilment'
import { OutletSelect, useCrossLocationFulfilmentGate } from '../OutletSelect'
import { useSpecialOrderProduct } from '../../../../special-order-product/useSpecialOrderProduct'
import type { LineItemDisplayFulfilmentProps } from './useLineItemDisplayFulfilmentModel'

export const LineItemDisplayFulfilmentLegacy: React.FC<LineItemDisplayFulfilmentProps> = ({
  lineItem: defaultLineItem,
  onSelectFulfilmentType,
}) => {
  const t = useT()

  const saleInventoryData = useSaleInventoryData()
  const dispatch = useAppDispatch()

  const {
    currentLineItem,
    fulfilmentDetail,
    setFulfilmentType,
    setFulfilmentOutletId = () => {},
    setFulfilmentNote,
    isDisabled,
    isFulfilmentOutletDisabled,
  } = useSaleFulfilment(defaultLineItem.localId)
  const isSpecialOrderProduct = useSpecialOrderProduct(currentLineItem)
  // Auto populate fulfillment addresses based on customer addresses
  useAutoPopulateFulfillmentAddresses(fulfilmentDetail)

  const isEcomOrder = useAppSelector(selectIsEcomOrder)
  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()

  const shouldShowOutletSelect =
    canUseCrossLocationFulfilment &&
    isDefinedFulfilmentType(currentLineItem?.fulfilmentType)

  const handleSelectedOutletIdChange = useCallback(
    (outletId?: string) => {
      if (!outletId || currentLineItem?.fulfilmentOutletId) {
        return
      }

      setFulfilmentOutletId(outletId)
    },
    [currentLineItem?.fulfilmentOutletId, setFulfilmentOutletId]
  )

  const handleNoteErrorsChange = (errors: NoteError[]) => {
    if (errors.length === 0) {
      setLineItemFulfilmentNoteError({
        id: currentLineItem.localId,
        error: null,
      })

      return
    }

    setLineItemFulfilmentNoteError({
      id: currentLineItem.localId,
      error: errors[0],
    })
  }

  const shouldShowFulfilmentAddress =
    currentLineItem?.fulfilmentType === FulfilmentType.DELIVERY && !isEcomOrder

  const ffSpecialOrder = getFeatureFromCache('inventory_special_orders')
  const shouldShowSpecialOrderMessage =
    ffSpecialOrder &&
    isDefinedFulfilmentType(currentLineItem?.fulfilmentType) &&
    Boolean(currentLineItem && isSpecialOrderProduct(currentLineItem))

  return (
    <Stack space={4}>
      <OutdatedTaxCategoryWarning lineItem={defaultLineItem} />
      <FulfilmentSelect
        showUnspecified
        disabled={isDisabled}
        onTypeChange={type => {
          const fulfilmentOutletId = isDefinedFulfilmentType(type)
            ? currentLineItem?.fulfilmentOutletId
            : undefined

          setFulfilmentType(
            type,
            fulfilmentDetail?.note || '',
            fulfilmentOutletId
          )
          if (onSelectFulfilmentType) {
            onSelectFulfilmentType(type, fulfilmentOutletId)
          }
          if (!isDefinedFulfilmentType(type)) {
            saleInventoryData.enableInventoryValidation(currentLineItem)
            if (
              Number(currentLineItem.quantity) > 0 &&
              saleInventoryData.isInventoryTracked(currentLineItem) &&
              !saleInventoryData.hasInventory(currentLineItem)
            ) {
              dispatch(openFulfilmentLineItemPopover(currentLineItem.localId))
            }
          } else {
            saleInventoryData.disableInventoryValidation(currentLineItem)
          }
          saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
        }}
        defaultType={currentLineItem?.fulfilmentType}
        stretched
        message={t(
          'You can select one method per sale. To set another method, create another sale.',
          {
            _context: 'Note explaining how fulfillment methods work',
          }
        )}
        outOfStockMessage={
          shouldShowSpecialOrderMessage
            ? t(
                'This product is out of stock. If incoming stock is insufficient, it will be available for special order and ready to be fulfilled upon restock.',
                {
                  _context: 'Special order product info message',
                }
              )
            : undefined
        }
      />

      {shouldShowOutletSelect && (
        <OutletSelect
          className="vd-mb5"
          disabled={isFulfilmentOutletDisabled}
          includeAvailability
          initialSelectedOutletId={currentLineItem?.fulfilmentOutletId}
          onOutletChange={outlet => setFulfilmentOutletId(outlet.id)}
          onSelectedOutletIdChange={handleSelectedOutletIdChange}
          productId={currentLineItem?.product.id}
          showLabel
        />
      )}

      {shouldShowFulfilmentAddress && (
        <LineItemFulfilmentAddress
          fulfillmentDetail={fulfilmentDetail}
          lineItemLocalId={defaultLineItem.localId}
          isModalOpeningOnError={fulfilmentDetail?.shouldAddNewAddress}
        />
      )}

      {!!currentLineItem?.fulfilmentType &&
        currentLineItem.fulfilmentType !== FulfilmentType.UNSPECIFIED && (
          <LineItemFulfilmentNote
            note={fulfilmentDetail?.note || ''}
            isDisabled={isDisabled}
            onChange={setFulfilmentNote}
            onErrorsChange={handleNoteErrorsChange}
          />
        )}
    </Stack>
  )
}
